import React, { useState, useEffect } from 'react'
import { Save, FolderOpen, Download, Upload, Trash2, Plus, Search } from 'lucide-react'
import usePlayStore from '../../stores/playStore'
import AVAILABLE_TAGS from '../../config/tags'
import { uploadGifToS3 } from '../../services/s3Service'
import { updatePlayGif } from '../../services/playsApi'

const PlayManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('save') // 'save', 'load', 'download'
  const [playName, setPlayName] = useState('')
  const [playDescription, setPlayDescription] = useState('')
  const [sportType, setSportType] = useState('soccer')
  const [isPublic, setIsPublic] = useState(false)
  const [selectedTags, setSelectedTags] = useState([])
  const [tagSearch, setTagSearch] = useState('')
  const [showTagDropdown, setShowTagDropdown] = useState(false)
  const [savedPlays, setSavedPlays] = useState([])
  const [publicPlays, setPublicPlays] = useState([])
  const [loadView, setLoadView] = useState('my') // 'my', 'public', 'file'
  const [loadSearch, setLoadSearch] = useState('') // Search term for filtering plays
  const [isLoadingPlays, setIsLoadingPlays] = useState(false)
  const [isLoadingPublicPlays, setIsLoadingPublicPlays] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  
  const { 
    currentPlay, 
    setCurrentPlay, 
    savePlay, 
    loadPlay, 
    getSavedPlays,
    getPublicPlays,
    currentTime,
    setCurrentTime,
    isPlaying,
    setPlaying,
    duration,
    deletePlay,
    clearTeams,
    clearProps,
    clearAnimations,
    resetPlayName,
    clearSelectedEntity,
    importPlay
  } = usePlayStore()

  // Load saved plays when modal opens for load
  useEffect(() => {
    if (showModal && modalType === 'load') {
      if (loadView === 'my') {
        loadSavedPlays()
      } else if (loadView === 'public') {
        loadPublicPlays()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, modalType, loadView])

  // Close tag dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTagDropdown && !event.target.closest('.tag-search-container')) {
        setShowTagDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTagDropdown])

  const loadSavedPlays = async () => {
    setIsLoadingPlays(true)
    try {
      const plays = await getSavedPlays()
      setSavedPlays(plays)
    } catch (error) {
      console.error('Error loading plays:', error)
      setSavedPlays([])
    } finally {
      setIsLoadingPlays(false)
    }
  }

  const loadPublicPlays = async () => {
    setIsLoadingPublicPlays(true)
    try {
      const plays = await getPublicPlays()
      setPublicPlays(plays)
    } catch (error) {
      console.error('Error loading public plays:', error)
      setPublicPlays([])
    } finally {
      setIsLoadingPublicPlays(false)
    }
  }

  const handleSave = async () => {
    if (playName.trim()) {
      setIsSaving(true)
      try {
        // Check if a play with the same name already exists
        const existingPlay = savedPlays.find(play => play.name === playName.trim())
        
        if (existingPlay) {
          // Show confirmation dialog
          const shouldOverwrite = confirm(
            `A play named "${playName.trim()}" already exists. Do you want to overwrite it?`
          )
          
          if (!shouldOverwrite) {
            setIsSaving(false)
            return // User cancelled, don't save
          }
        }
        
        const updatedPlay = {
          ...currentPlay,
          name: playName,
          description: playDescription,
          sportType: sportType,
          tags: selectedTags
        }
        setCurrentPlay(updatedPlay)
        const savedPlay = await savePlay(existingPlay ? existingPlay.id : null, isPublic) // Pass existing ID and public flag
        setShowModal(false)
        setPlayName('')
        setPlayDescription('')
        setSportType('soccer')
        setIsPublic(false)
        setSelectedTags([])
        setTagSearch('')
        setShowTagDropdown(false)
        // Reload plays list
        await loadSavedPlays()
        
        // Generate GIF automatically after save (async, don't block)
        if (savedPlay && savedPlay.id) {
          // Wait a bit for the scene to be ready, then generate
          setTimeout(async () => {
            try {
              // Check if scene is ready
              if (!window.phaserScene || !window.phaserScene.game || !window.phaserScene.game.canvas) {
                console.warn('Phaser scene not ready, retrying in 2 seconds...')
                setTimeout(async () => {
                  try {
                    setIsAutoGenerating(true)
                    setAutoGenerateProgress(0)
                    setAutoGenerateStatus('Preparing play...')
                    
                    const success = await generateAndUploadGif(
                      savedPlay.id, 
                      savedPlay.name, 
                      true, // skipDownload
                      (progress, status) => {
                        setAutoGenerateProgress(progress)
                        setAutoGenerateStatus(status)
                      }
                    )
                    
                    if (success) {
                      setAutoGenerateStatus('Play saved successfully!')
                      setTimeout(() => setIsAutoGenerating(false), 2000)
                    } else {
                      console.warn('GIF auto-generation completed but upload may have failed')
                      setAutoGenerateStatus('Save completed with errors')
                      setTimeout(() => setIsAutoGenerating(false), 3000)
                    }
                  } catch (error) {
                    console.error('Auto GIF generation failed on retry:', error)
                    setAutoGenerateStatus('Save failed')
                    setTimeout(() => setIsAutoGenerating(false), 2000)
                  }
                }, 2000)
                return
              }
              
              setIsAutoGenerating(true)
              setAutoGenerateProgress(0)
              setAutoGenerateStatus('Preparing play...')
              
              const success = await generateAndUploadGif(
                savedPlay.id, 
                savedPlay.name, 
                true, // skipDownload
                (progress, status) => {
                  setAutoGenerateProgress(progress)
                  setAutoGenerateStatus(status)
                }
              )
              
              if (success) {
                setAutoGenerateStatus('Play saved successfully!')
                setTimeout(() => setIsAutoGenerating(false), 2000)
              } else {
                console.warn('GIF auto-generation completed but upload may have failed')
                setAutoGenerateStatus('Save completed with errors')
                setTimeout(() => setIsAutoGenerating(false), 3000)
              }
            } catch (error) {
              console.error('Auto GIF generation failed:', error)
              setAutoGenerateStatus('Save failed')
              setTimeout(() => setIsAutoGenerating(false), 2000)
            }
          }, 1000) // Wait 1 second for scene to be ready
        }
      } catch (error) {
        console.error('Error saving play:', error)
        // Error is already shown in playStore
      } finally {
        setIsSaving(false)
      }
    }
  }

  const handleLoad = async (playId) => {
    try {
      // Determine if this is from "My Plays" (user's own) or "Public Plays" tab
      const isUserOwnPlay = loadView === 'my'
      await loadPlay(playId, isUserOwnPlay)
      setShowModal(false)
    } catch (error) {
      console.error('Error loading play:', error)
      // Error is already shown in playStore
    }
  }

  const handleDelete = async (playId, e) => {
    e.stopPropagation()
    if (confirm('Are you sure you want to delete this play?')) {
      try {
        await deletePlay(playId)
        // Reload plays list
        await loadSavedPlays()
      } catch (error) {
        console.error('Error deleting play:', error)
        // Error is already shown in playStore
      }
    }
  }

  const handleExport = () => {
    const state = usePlayStore.getState()
    
    // Restore teams to their original starting positions before exporting
    const teamsWithOriginalPositions = state.teams.map(team => {
      // Find the first animation for this team to get the starting position
      const firstAnimation = state.animations
        .filter(anim => anim.teamId === team.id)
        .sort((a, b) => a.startTime - b.startTime)[0]
      
      if (firstAnimation) {
        // Use the starting position from the first animation
        return {
          ...team,
          x: firstAnimation.startX,
          y: firstAnimation.startY
        }
      }
      
      // If no animation, use current position
      return team
    })
    
    // Restore props to their original starting positions before exporting
    const propsWithOriginalPositions = state.props.map(prop => {
      // Find the first animation for this prop to get the starting position
      const firstAnimation = state.animations
        .filter(anim => anim.propId === prop.id)
        .sort((a, b) => a.startTime - b.startTime)[0]
      
      if (firstAnimation) {
        // Use the starting position from the first animation
        return {
          ...prop,
          x: firstAnimation.startX,
          y: firstAnimation.startY
        }
      }
      
      // If no animation, use current position
      return prop
    })
    
    const playData = {
      ...currentPlay,
      teams: teamsWithOriginalPositions,
      props: propsWithOriginalPositions,
      animations: state.animations,
      fieldSettings: state.fieldSettings
    }
    
    const dataStr = JSON.stringify(playData, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = `${playData.name || 'soccer-play'}.json`
    link.click()
    
    URL.revokeObjectURL(url)
    setShowModal(false)
  }

  // Validate imported play data structure
  const validatePlayData = (playData) => {
    const errors = []
    
    if (!playData || typeof playData !== 'object') {
      errors.push('Play data must be an object')
      return errors
    }
    
    if (playData.teams && !Array.isArray(playData.teams)) {
      errors.push('Teams must be an array')
    }
    
    if (playData.props && !Array.isArray(playData.props)) {
      errors.push('Props must be an array')
    }
    
    if (playData.animations && !Array.isArray(playData.animations)) {
      errors.push('Animations must be an array')
    }
    
    // Validate team structure if teams exist
    if (playData.teams && Array.isArray(playData.teams)) {
      playData.teams.forEach((team, index) => {
        if (!team.id) errors.push(`Team at index ${index} missing id`)
        if (typeof team.x !== 'number' || isNaN(team.x)) {
          errors.push(`Team at index ${index} has invalid x coordinate`)
        }
        if (typeof team.y !== 'number' || isNaN(team.y)) {
          errors.push(`Team at index ${index} has invalid y coordinate`)
        }
      })
    }
    
    // Validate prop structure if props exist
    if (playData.props && Array.isArray(playData.props)) {
      playData.props.forEach((prop, index) => {
        if (!prop.id) errors.push(`Prop at index ${index} missing id`)
        if (typeof prop.x !== 'number' || isNaN(prop.x)) {
          errors.push(`Prop at index ${index} has invalid x coordinate`)
        }
        if (typeof prop.y !== 'number' || isNaN(prop.y)) {
          errors.push(`Prop at index ${index} has invalid y coordinate`)
        }
      })
    }
    
    return errors
  }

  const handleImport = (e) => {
    const file = e.target.files[0]
    if (!file) {
      return
    }
    
    // Validate file type
    if (!file.name.endsWith('.json')) {
      alert('Please select a JSON file')
      return
    }
    
    const reader = new FileReader()
    
    reader.onerror = () => {
      alert('Error reading file. Please try again.')
    }
    
    reader.onload = (event) => {
      try {
        const playData = JSON.parse(event.target.result)
        
        // Validate the imported data
        const validationErrors = validatePlayData(playData)
        if (validationErrors.length > 0) {
          alert(`Invalid play data:\n${validationErrors.join('\n')}`)
          return
        }
        
        // Use the new importPlay action instead of direct state mutation
        importPlay(playData)
        setShowModal(false)
        setLoadView('my') // Reset to default view
      } catch (error) {
        console.error('Error importing play file:', error)
        if (error instanceof SyntaxError) {
          alert('Invalid JSON format. Please check the file and try again.')
        } else {
          alert(`Error importing play file: ${error.message}`)
        }
      }
    }
    
    reader.readAsText(file)
  }

  const openModal = (type) => {
    setModalType(type)
    setShowModal(true)
    if (type === 'save') {
      // Use current play name as default, or empty string if not set
      setPlayName(currentPlay.name || '')
      setPlayDescription(currentPlay.description || '')
      setSportType(currentPlay.sportType || 'soccer')
      setIsPublic(currentPlay.isPublic || false)
      setSelectedTags(currentPlay.tags || [])
      setTagSearch('')
      setShowTagDropdown(false)
    } else if (type === 'load') {
      // Reset to 'my' view when opening load modal
      setLoadView('my')
      setLoadSearch('') // Reset search when opening modal
    }
  }

  // Filter plays based on search term (searches name, description, tags, and sportType)
  const filterPlays = (plays, searchTerm) => {
    if (!searchTerm.trim()) {
      return plays
    }
    
    const searchLower = searchTerm.toLowerCase().trim()
    return plays.filter(play => {
      // Search in name
      if (play.name?.toLowerCase().includes(searchLower)) {
        return true
      }
      
      // Search in description
      if (play.description?.toLowerCase().includes(searchLower)) {
        return true
      }
      
      // Search in sport type
      if (play.sportType?.toLowerCase().includes(searchLower)) {
        return true
      }
      
      // Search in tags
      if (play.tags && Array.isArray(play.tags)) {
        const tagMatch = play.tags.some(tag => 
          tag.toLowerCase().includes(searchLower)
        )
        if (tagMatch) {
          return true
        }
      }
      
      return false
    })
  }

  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    )
    setTagSearch('')
    setShowTagDropdown(false)
  }

  const removeTag = (tag) => {
    setSelectedTags(prev => prev.filter(t => t !== tag))
  }

  // Filter tags based on search input
  const filteredTags = React.useMemo(() => {
    if (!tagSearch.trim()) {
      // If search is empty, show all tags except selected ones
      return AVAILABLE_TAGS.filter(tag => !selectedTags.includes(tag))
    }
    // Filter tags that match the search and aren't already selected
    const searchLower = tagSearch.toLowerCase().trim()
    return AVAILABLE_TAGS.filter(tag => {
      const tagLower = tag.toLowerCase()
      return tagLower.includes(searchLower) && !selectedTags.includes(tag)
    })
  }, [tagSearch, selectedTags])

  // --- Download (GIF) ---
  const [isGenerating, setIsGenerating] = useState(false)
  const [generateStatus, setGenerateStatus] = useState('')
  
  // Auto-generation progress modal
  const [isAutoGenerating, setIsAutoGenerating] = useState(false)
  const [autoGenerateProgress, setAutoGenerateProgress] = useState(0)
  const [autoGenerateStatus, setAutoGenerateStatus] = useState('')

  const ensureGifshotLoaded = () => new Promise((resolve, reject) => {
    if (window.gifshot) return resolve()
    const script = document.createElement('script')
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/gifshot/0.3.2/gifshot.min.js'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load GIF library'))
    document.body.appendChild(script)
  })

  const waitNextFrame = () => new Promise((r) => requestAnimationFrame(() => r()))

  // Generate and upload GIF (extracted for reuse)
  const generateAndUploadGif = async (playId, playName, skipDownload = false, onProgress = null) => {
    if (!window.phaserScene || !window.phaserScene.game || !window.phaserScene.game.canvas) {
      console.warn('Phaser scene not ready for GIF capture')
      return false
    }

    // Get current state from store (before try block so it's available in finally)
    const storeState = usePlayStore.getState()
    const wasPlaying = storeState.isPlaying
    const originalTime = typeof storeState.currentTime === 'number' ? storeState.currentTime : 0
    const totalDuration = typeof storeState.duration === 'number' ? storeState.duration : 10

    // Hide canvas during generation
    const canvas = window.phaserScene.game.canvas
    const canvasElement = canvas.parentElement || canvas
    const originalOpacity = canvasElement.style.opacity || '1'
    const originalVisibility = canvasElement.style.visibility || ''
    canvasElement.style.opacity = '0'
    canvasElement.style.visibility = 'hidden'

    try {
      await ensureGifshotLoaded()
      
      setPlaying(false)

      const fps = 10
      const frameCount = Math.max(1, Math.floor(totalDuration * fps))
      const step = totalDuration / frameCount
      const frames = []

      const gifWidth = canvas.width
      const gifHeight = canvas.height

      // Update progress for preparation
      if (onProgress) onProgress(1, 'Preparing play...')

      // Generate frames by stepping the timeline
      for (let i = 0; i <= frameCount; i++) {
        const t = Math.min(totalDuration, i * step)
        setCurrentTime(parseFloat(t.toFixed(3)))
        // allow scene to render
        await waitNextFrame()
        await waitNextFrame()
        frames.push(canvas.toDataURL('image/png'))
        
        // Update progress during frame capture (1% to 25%)
        if (onProgress) {
          const progress = 1 + Math.round((i / frameCount) * 24)
          onProgress(progress, 'Processing play...')
        }
      }

      // Update progress for encoding
      if (onProgress) onProgress(26, 'Finalizing play...')

      // Encode GIF with progress updates
      const gifDataUrl = await new Promise((resolve, reject) => {
        try {
          window.gifshot.createGIF({
            images: frames,
            gifWidth,
            gifHeight,
            interval: 1 / fps,
            numFrames: frames.length,
            sampleInterval: 10,
            frameDuration: 1 / fps,
            progressCallback: function(progress) {
              // Progress is a decimal number from 0 to 1 (not 0-100)
              let gifshotProgress = 0
              if (typeof progress === 'number') {
                gifshotProgress = progress // Already a decimal 0-1
              } else if (progress && typeof progress.progress === 'number') {
                gifshotProgress = progress.progress
              } else if (progress && typeof progress.percentComplete === 'number') {
                // If it's a percentage (0-100), convert to decimal
                gifshotProgress = progress.percentComplete / 100
              } else if (progress && typeof progress.percent === 'number') {
                // If it's a percentage (0-100), convert to decimal
                gifshotProgress = progress.percent / 100
              } else {
                // Unknown progress format, skip update
                return
              }
              
              // Map decimal (0-1) to our 26-75% range for "Finalizing play..."
              // Range size is 49 (75 - 26), so multiply by 49 and add 26
              if (onProgress && gifshotProgress >= 0) {
                const mappedProgress = 26 + Math.round(gifshotProgress * 49) // Maps 0-1 to 26-75
                onProgress(mappedProgress, 'Finalizing play...')
              }
            }
          }, function (obj) {
            if (!obj || obj.error) {
              console.error('GIF encoding error:', obj?.error)
              reject(new Error('GIF encoding failed'))
            } else {
              // Ensure we're at 75% when encoding completes
              if (onProgress) onProgress(75, 'Finalizing play...')
              resolve(obj.image)
            }
          })
        } catch (error) {
          console.error('Error calling createGIF:', error)
          reject(error)
        }
      })

      // Update progress for upload
      if (onProgress) onProgress(90, 'Uploading play...')

      // Download locally (unless skipDownload is true)
      if (!skipDownload) {
        const link = document.createElement('a')
        link.href = gifDataUrl
        const base = (playName && playName.trim()) ? playName.trim() : 'soccer-play'
        link.download = `${base}.gif`
        document.body.appendChild(link)
        link.click()
        link.remove()
      }

      // Upload to S3 and update DynamoDB
      try {
        const { url } = await uploadGifToS3(gifDataUrl, playId)
        await updatePlayGif(playId, url, 'completed')
        
        // Update current play in store if it matches
        const storeState = usePlayStore.getState()
        if (storeState.currentPlay.id === playId) {
          setCurrentPlay({
            ...storeState.currentPlay,
            gifUrl: url,
            gifStatus: 'completed',
            gifGeneratedAt: new Date().toISOString()
          })
        }
        
        if (onProgress) onProgress(100, 'Play saved!')
        return true
      } catch (uploadError) {
        console.error('Failed to upload GIF to S3:', uploadError)
        await updatePlayGif(playId, null, 'failed')
        if (onProgress) onProgress(100, 'Save failed')
        return false
      }
    } catch (e) {
      console.error('Failed to generate GIF:', e)
      try {
        await updatePlayGif(playId, null, 'failed')
      } catch (updateError) {
        console.error('Failed to update GIF status:', updateError)
      }
      if (onProgress) onProgress(100, 'Save failed')
      return false
    } finally {
      // Restore canvas visibility
      canvasElement.style.opacity = originalOpacity
      canvasElement.style.visibility = originalVisibility
      // Restore state
      setCurrentTime(originalTime)
      setPlaying(wasPlaying)
    }
  }

  const handleDownloadGif = async () => {
    // Check if GIF already exists
    if (currentPlay.gifUrl && currentPlay.gifStatus === 'completed') {
      // Download existing GIF
      const link = document.createElement('a')
      link.href = currentPlay.gifUrl
      link.download = `${currentPlay.name || 'soccer-play'}.gif`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      link.remove()
      return
    }
    
    if (!currentPlay.id) {
      alert('Please save the play first before generating a GIF.')
      return
    }

    try {
      setIsGenerating(true)
      setGenerateStatus('Preparing...')
      
      const success = await generateAndUploadGif(
        currentPlay.id, 
        currentPlay.name, 
        false,
        (progress, status) => {
          setGenerateStatus(status)
        }
      )
      
      if (success) {
        setGenerateStatus('Uploaded successfully!')
        setTimeout(() => {
          setShowModal(false)
          setGenerateStatus('')
        }, 2000)
      } else {
        setGenerateStatus('GIF generated (upload failed)')
        setTimeout(() => {
          setGenerateStatus('')
        }, 3000)
      }
    } catch (e) {
      console.error(e)
      alert('Failed to generate GIF.')
      setGenerateStatus('')
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <>
      <div className="flex items-center space-x-4">
        {/* Play Name Input */}
        <div className="flex items-center space-x-2">
          <label htmlFor="playName" className="text-sm font-medium text-gray-700">
            Play Name:
          </label>
          <input
            id="playName"
            type="text"
            value={currentPlay.name}
            onChange={(e) => setCurrentPlay({ ...currentPlay, name: e.target.value })}
            placeholder="Enter play name..."
            className="px-3 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
        <button
          onClick={() => openModal('save')}
          className="flex items-center justify-center space-x-1 w-32 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
        >
          <Save size={14} />
          <span>Save</span>
        </button>
        
        <button
          onClick={() => openModal('load')}
          className="flex items-center justify-center space-x-1 w-32 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          <FolderOpen size={14} />
          <span>Load</span>
        </button>
        
        <button
          onClick={() => openModal('download')}
          className="flex items-center justify-center space-x-1 w-32 px-3 py-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors"
        >
          <Download size={14} />
          <span>Download</span>
        </button>
        
        <button
          onClick={() => {
            if (confirm('Clear all teams, props, and animations?')) {
              clearSelectedEntity() // Clear any selected entity highlight first
              // Small delay to ensure visual update happens before clearing sprites
              setTimeout(() => {
                clearTeams()
                clearProps()
                clearAnimations()
                setPlaying(false)  // Stop timeline playback
                setCurrentTime(0)  // Reset timeline to 0s
                resetPlayName()    // Reset play name to "Untitled Play"
              }, 10)
            }
          }}
          className="flex items-center justify-center space-x-1 w-32 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
        >
          <Trash2 size={14} />
          <span>Clear</span>
        </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            {modalType === 'save' && (
              <>
                <h3 className="text-lg font-semibold mb-4">Save Play</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Play Name
                    </label>
                    <input
                      type="text"
                      value={playName}
                      onChange={(e) => setPlayName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter play name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={playDescription}
                      onChange={(e) => setPlayDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Enter play description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sport Type
                    </label>
                    <select
                      value={sportType}
                      onChange={(e) => setSportType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="soccer">Soccer</option>
                      <option value="football">Football</option>
                      <option value="basketball">Basketball</option>
                      <option value="hockey">Hockey</option>
                      <option value="lacrosse">Lacrosse</option>
                      <option value="rugby">Rugby</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    <div className="relative tag-search-container">
                      <input
                        type="text"
                        value={tagSearch}
                        onChange={(e) => {
                          setTagSearch(e.target.value)
                          setShowTagDropdown(true)
                        }}
                        onFocus={() => setShowTagDropdown(true)}
                        placeholder="Search and select tags..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {showTagDropdown && filteredTags.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {filteredTags.map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => toggleTag(tag)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      )}
                      {showTagDropdown && tagSearch && filteredTags.length === 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 text-sm text-gray-500">
                          No matching tags found
                        </div>
                      )}
                    </div>
                    {selectedTags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedTags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-3 py-1 text-sm bg-blue-500 text-white rounded-full"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => removeTag(tag)}
                              className="ml-2 hover:text-gray-200 focus:outline-none"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isPublic"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="isPublic" className="text-sm font-medium text-gray-700">
                      Make this play public
                    </label>
                  </div>
                  {isPublic && (
                    <p className="text-xs text-gray-500">
                      Public plays can be viewed and loaded by all users
                    </p>
                  )}
                </div>
                <div className="flex justify-end space-x-2 mt-6">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            )}

            {modalType === 'load' && (
              <>
                <h3 className="text-lg font-semibold mb-4">Load Play</h3>
                
                {/* Tab Navigation */}
                <div className="flex space-x-2 mb-4 border-b border-gray-200">
                  <button
                    onClick={() => {
                      setLoadView('my')
                      setLoadSearch('') // Reset search when switching tabs
                    }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      loadView === 'my'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    My Plays
                  </button>
                  <button
                    onClick={() => {
                      setLoadView('public')
                      setLoadSearch('') // Reset search when switching tabs
                    }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      loadView === 'public'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Public Plays
                  </button>
                  <button
                    onClick={() => {
                      setLoadView('file')
                      setLoadSearch('') // Reset search when switching tabs
                    }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      loadView === 'file'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Load from File
                  </button>
                </div>

                {/* My Plays View */}
                {loadView === 'my' && (
                  <div className="space-y-2">
                    {/* Search Input */}
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        value={loadSearch}
                        onChange={(e) => setLoadSearch(e.target.value)}
                        placeholder="Search by name, description, tags, or sport type..."
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {isLoadingPlays ? (
                        <p className="text-gray-500 text-center py-4">Loading plays...</p>
                      ) : (() => {
                        const filteredPlays = filterPlays(savedPlays, loadSearch)
                        return filteredPlays.length === 0 ? (
                          <p className="text-gray-500 text-center py-4">
                            {loadSearch.trim() ? 'No plays match your search' : 'No saved plays'}
                          </p>
                        ) : (
                          filteredPlays.map((play) => (
                        <div
                          key={play.id}
                          onClick={() => handleLoad(play.id)}
                          className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer flex justify-between items-start"
                        >
                          <div>
                            <div className="font-medium flex items-center space-x-2">
                              <span>{play.name}</span>
                              {play.isPublic && (
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                  Public
                                </span>
                              )}
                              {play.sportType && (
                                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded capitalize">
                                  {play.sportType}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{play.description}</div>
                            {play.tags && play.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {play.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="text-xs text-gray-400 mt-1">
                              {new Date(play.updatedAt).toLocaleString()}
                            </div>
                          </div>
                          <button
                            onClick={(e) => handleDelete(play.id, e)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                          ))
                        )
                      })()}
                    </div>
                  </div>
                )}

                {/* Public Plays View */}
                {loadView === 'public' && (
                  <div className="space-y-2">
                    {/* Search Input */}
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        value={loadSearch}
                        onChange={(e) => setLoadSearch(e.target.value)}
                        placeholder="Search by name, description, tags, or sport type..."
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {isLoadingPublicPlays ? (
                        <p className="text-gray-500 text-center py-4">Loading public plays...</p>
                      ) : (() => {
                        const filteredPlays = filterPlays(publicPlays, loadSearch)
                        return filteredPlays.length === 0 ? (
                          <p className="text-gray-500 text-center py-4">
                            {loadSearch.trim() ? 'No plays match your search' : 'No public plays available'}
                          </p>
                        ) : (
                          filteredPlays.map((play) => (
                        <div
                          key={play.id}
                          onClick={() => handleLoad(play.id)}
                          className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <div>
                            <div className="font-medium flex items-center space-x-2">
                              <span>{play.name}</span>
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                Public
                              </span>
                              {play.sportType && (
                                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded capitalize">
                                  {play.sportType}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{play.description}</div>
                            {play.tags && play.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {play.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="text-xs text-gray-400 mt-1">
                              {new Date(play.updatedAt).toLocaleString()}
                            </div>
                          </div>
                        </div>
                          ))
                        )
                      })()}
                    </div>
                  </div>
                )}

                {/* Load from File View */}
                {loadView === 'file' && (
                  <div className="space-y-4">
                    <p className="text-gray-600 mb-4">
                      Import a play from a JSON file.
                    </p>
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImport}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </>
            )}

            {modalType === 'download' && (
              <>
                <h3 className="text-lg font-semibold mb-4">Download</h3>
                <div className="space-y-4">
                  <div className="p-3 border rounded flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium">Animated GIF</div>
                      <div className="text-xs text-gray-500">
                        {currentPlay.gifUrl && currentPlay.gifStatus === 'completed' 
                          ? 'GIF available in cloud' 
                          : currentPlay.gifStatus === 'pending'
                          ? 'GIF generation pending'
                          : currentPlay.gifStatus === 'failed'
                          ? 'GIF generation failed'
                          : 'Exports the full timeline'}
                      </div>
                      {generateStatus && (
                        <div className="text-xs text-blue-600 mt-1">{generateStatus}</div>
                      )}
                    </div>
                    <button
                      onClick={handleDownloadGif}
                      disabled={isGenerating}
                      className="px-3 py-1 rounded bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-60"
                    >
                      {isGenerating 
                        ? 'Generating…' 
                        : currentPlay.gifUrl && currentPlay.gifStatus === 'completed'
                        ? 'Download'
                        : 'Generate & Download'}
                    </button>
                  </div>
                  <div className="p-3 border rounded flex items-center justify-between">
                    <div>
                      <div className="font-medium">Video (MP4)</div>
                      <div className="text-xs text-gray-500">Coming soon</div>
                    </div>
                    <button
                      disabled
                      className="px-3 py-1 rounded bg-gray-200 text-gray-500 cursor-not-allowed"
                    >
                      Download
                    </button>
                  </div>
                  <div className="p-3 border rounded flex items-center justify-between">
                    <div>
                      <div className="font-medium">To File (JSON)</div>
                      <div className="text-xs text-gray-500">Export play as JSON file to share or backup</div>
                    </div>
                    <button
                      onClick={() => {
                        handleExport()
                        setShowModal(false)
                      }}
                      className="px-3 py-1 rounded bg-purple-500 text-white hover:bg-purple-600"
                    >
                      Download
                    </button>
                  </div>
                  {isGenerating && (
                    <div className="text-xs text-gray-600">{generateStatus}</div>
                  )}
                </div>
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => !isGenerating && setShowModal(false)}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-60"
                    disabled={isGenerating}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Auto-Generation Progress Modal */}
      {isAutoGenerating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Saving Play</h3>
            
            <div className="mb-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${autoGenerateProgress}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-2 text-center">
                {autoGenerateStatus}
              </p>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              This may take a moment...
            </p>
          </div>
        </div>
      )}
    </>
  )
}

export default PlayManager
